import type { Stage } from "@/components/new-look/labels";
import { cn } from "@/lib/utils";
import { estimateParts, formatMinutes, isDone, useBlueprintParts, type PartTiming } from "./progress-ring";

/**
 * "How this idea grew" from the design: one card per stage. Idea shows what
 * you said, Blueprint its size and time estimate, Build every part with its
 * own task progress, Done what shipping means.
 */

const CARD = "flex min-w-0 flex-col gap-2 rounded-2xl border border-border bg-background p-4";
const LABEL = "text-xs font-bold uppercase tracking-wide";

function excerpt(text: string, max = 160): string {
  const t = text.trim().replace(/\s+/g, " ");
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

export function partProgress(part: PartTiming): { done: number; total: number } {
  const total = part._count?.tasks ?? 0;
  if (isDone(part)) return { done: total, total };
  return { done: Math.min(total, part.completedTaskCount ?? 0), total };
}

export function IdeaGrowth({
  said,
  epicId,
  stage,
  live,
}: {
  said: string;
  epicId: string | null;
  stage: Stage;
  live: boolean;
}) {
  const parts = useBlueprintParts(epicId, live);
  const tasks = parts.reduce((sum, p) => sum + (p._count?.tasks ?? 0), 0);
  const estimate = estimateParts(parts);
  const shipped = stage === "done";

  return (
    <section aria-labelledby="idea-growth" className="flex flex-col gap-3">
      <h2 id="idea-growth" className="text-lg font-semibold text-foreground">
        How this idea grew
      </h2>
      <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <li className={CARD}>
          <span className={cn(LABEL, "text-purple-600 dark:text-purple-300")}>1 · Idea</span>
          <span className="text-sm text-muted-foreground">You said</span>
          <p className="text-sm text-foreground">{said ? excerpt(said) : "Nothing yet."}</p>
        </li>

        <li className={CARD}>
          <span className={cn(LABEL, "text-sky-600 dark:text-sky-300")}>2 · Blueprint</span>
          {parts.length > 0 ? (
            <p className="text-sm text-foreground">
              {`${String(parts.length)} ${parts.length === 1 ? "part" : "parts"}, ${String(tasks)} ${tasks === 1 ? "task" : "tasks"}`}
              {estimate ? `, about ${formatMinutes(estimate.minutesLeft + estimate.minutesTaken)} in all` : ""}.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Parts, tasks and a time estimate, made once the idea is shaped.
            </p>
          )}
        </li>

        <li className={CARD}>
          <span className={cn(LABEL, "text-blue-600 dark:text-blue-300")}>3 · Build</span>
          {parts.length > 0 ? (
            <ul className="flex flex-col gap-2.5">
              {parts.map((part) => {
                const { done, total } = partProgress(part);
                const pct = total > 0 ? Math.round((done / total) * 100) : isDone(part) ? 100 : 0;
                return (
                  <li key={part.id} className="flex flex-col gap-1">
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="min-w-0 truncate font-semibold text-foreground" title={part.title}>
                        {part.title}
                      </span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">{`${String(done)}/${String(total)}`}</span>
                    </div>
                    <div
                      role="progressbar"
                      aria-label={`${part.title}: ${String(done)} of ${String(total)} tasks`}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={pct}
                      className="h-1.5 overflow-hidden rounded-full bg-muted"
                    >
                      <div
                        className={cn("h-full rounded-full", pct === 100 ? "bg-green-600" : "bg-primary")}
                        style={{ width: `${String(pct)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Each part and its tasks, as the Builder works through them.</p>
          )}
        </li>

        <li className={CARD}>
          <span className={cn(LABEL, "text-green-700 dark:text-green-300")}>4 · Done</span>
          <p className={cn("text-sm", shipped ? "text-foreground" : "text-muted-foreground")}>
            Merged, live and checked. Nothing left for you.
          </p>
        </li>
      </ol>
    </section>
  );
}
